import { PunktValue } from "@/components/warehouse/PunktValue"
import type { CommissionMember } from "@/lib/actRequisites"
import { formatCommissionMember, formatUaDate } from "@/lib/warehouse"

/**
 * A row this sheet can print — either a live `OtkBlank` (the compose
 * dialog's own preview, before signing) or an accepted act's own embedded
 * `DestructionActItem` (`{ blankId, series, number }`, no live blank record
 * behind it any more once printed as a historical reprint) — both satisfy
 * this shape once mapped to it at the call site.
 */
export interface ActPrintSheetBlank {
  id: string
  series: string
  number: string
  /** Omitted when reprinting an accepted act — `DestructionActItem` carries no timestamp of its own. */
  updatedAt?: string | null
  statusReason?: string | null
}

/**
 * design-system.md's `ActPrintSheet` — `hidden print:block`, so it is
 * invisible on screen and the only thing that prints (dialog chrome is
 * `print:hidden` by default, see `components/ui/dialog.tsx`). Ported
 * field-for-field from the wireframe; two real-schema gaps, both stated
 * rather than papered over:
 *  - Neither `OtkBlank` nor `DestructionActItem` has a `punkt`/branch field
 *    at all yet (see `lib/warehouse.ts`), so the "Пункт (службове)" column
 *    always renders `PunktValue`'s `null` state ("Не розподілено") today,
 *    for every row.
 *  - `DestructionActItem` has no timestamp of its own; the "Дата зміни
 *    (службове)" column renders "—" when reprinting an already-accepted act.
 */
export function ActPrintSheet({
  actNumber,
  actDate,
  location,
  dateFrom,
  dateTo,
  commissionChair,
  commissionMembers,
  blanks,
  blankReasons,
  destructionPlace,
  destructionTime,
  destructionMethod,
  territorialBody,
  attestationStatement,
  attestedByUser,
}: {
  /** Omitted for the compose dialog's pre-signing preview — the real number is assigned server-side, atomically with the signed submission. */
  actNumber?: string
  actDate: string
  location: string
  dateFrom: string
  dateTo: string
  commissionChair: CommissionMember
  commissionMembers: CommissionMember[]
  blanks: ActPrintSheetBlank[]
  blankReasons?: Record<string, string>
  destructionPlace?: string
  destructionTime?: string
  destructionMethod?: string
  territorialBody?: string
  attestationStatement?: string
  attestedByUser?: string
}) {
  const dash = "—"
  return (
    <div className="hidden print:block print:p-10">
      <h1 className="text-center text-lg font-semibold">
        АКТ {actNumber ? `№ ${actNumber}` : "(попередній перегляд, номер буде присвоєно після підписання)"}
      </h1>
      <p className="text-center">
        знищення бланків протоколів перевірки технічного стану транспортного
        засобу
      </p>
      <p className="mt-6">
        Дата складання: {actDate ? formatUaDate(actDate) : dash}
      </p>
      <p>Місце складання: {location || dash}</p>
      <p>
        Комісія здійснила перевірку наявності пошкоджених або зіпсованих
        бланків за період з {dateFrom ? formatUaDate(dateFrom) : dash} по{" "}
        {dateTo ? formatUaDate(dateTo) : dash}.
      </p>
      <p className="mt-4 font-medium">Комісія у складі:</p>
      <p>Голова комісії: {formatCommissionMember(commissionChair) || dash}</p>
      {commissionMembers.length === 0 ? (
        <p>Члени комісії: {dash}</p>
      ) : (
        commissionMembers.map((m, i) => (
          <p key={`${m.role}-${m.name}-${i}`}>
            Член комісії {i + 1}: {formatCommissionMember(m) || dash}
          </p>
        ))
      )}
      <table className="mt-6 w-full border-collapse text-sm">
        <thead>
          <tr>
            <th className="border p-2 text-left">№ з/п</th>
            <th className="border p-2 text-left">Номер бланка</th>
            <th className="border p-2 text-left">Причина знищення</th>
            <th className="border p-2 text-left">
              Примітка (вклеюється вирізаний з бланка номер)
            </th>
            <th className="border p-2 text-left">Пункт (службове)</th>
            <th className="border p-2 text-left">Дата зміни (службове)</th>
          </tr>
        </thead>
        <tbody>
          {blanks.map((b, i) => (
            <tr key={b.id}>
              <td className="border p-2">{i + 1}</td>
              <td className="border p-2">
                {b.series} {b.number}
              </td>
              <td className="border p-2">
                {blankReasons?.[b.id] ?? b.statusReason ?? dash}
              </td>
              {/* Порожня клітинка навмисно: номер вклеюється на папері. */}
              <td className="border p-2">&nbsp;</td>
              <td className="border p-2">
                <PunktValue punkt={null} />
              </td>
              <td className="border p-2">{formatUaDate(b.updatedAt)}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <p className="mt-4">
        Указані в цьому акті бланки в кількості {blanks.length} шт. знищено.
      </p>
      <p>Місце знищення: {destructionPlace || dash}</p>
      <p>Час знищення: {destructionTime || dash}</p>
      <p>Спосіб знищення: {destructionMethod || dash}</p>
      {attestationStatement && (
        <p className="mt-4 text-sm">
          Підтвердження оператора{attestedByUser ? ` (${attestedByUser})` : ""}:{" "}
          {attestationStatement}
        </p>
      )}
      {territorialBody && (
        <p className="mt-2 text-sm">
          Копію цього акта належить надіслати територіальному органу з надання
          сервісних послуг МВС, який видав бланки: {territorialBody}.
        </p>
      )}
      <div className="mt-12 text-sm">
        <p>Голова комісії ____________ {commissionChair.name || dash}</p>
        {commissionMembers.map((m, i) => (
          <p className="mt-4" key={`sign-${m.name}-${i}`}>
            Член комісії ____________ {m.name || dash}
          </p>
        ))}
      </div>
    </div>
  )
}
